import { useState } from 'react';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { useCurrentUserId } from '@framework/auth';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { toast } from 'react-hot-toast';
import type { Workspace } from '@workspace/domain/workspace';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  workspace: Workspace;
  onClose(): void;
  shouldMoveToGroupPage: boolean;
};

export const WorkspaceDeleteConfirmModal: React.FC<Props> = ({
  isOpen,
  workspace,
  onClose,
  shouldMoveToGroupPage,
}: Props) => {
  const { t } = useTranslation();
  const currentUserId = useCurrentUserId();
  const navigate = useNavigate();
  const [checked, setChecked] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!checked) return;
    if (!currentUserId) return;

    try {
      await WorkspaceOperation.logicalDelete(workspace.id, currentUserId);
      toast.success(t('workspace.delete.success'));
      onClose();

      // サイドバーからワークスペースを削除する場合、削除対象のワークスペースを開いていない場合は
      // グループページへ遷移しなくて良いのでこの条件を入れている
      if (shouldMoveToGroupPage) {
        navigate(UserPagePathBuilder.groupPage(workspace.groupId));
      }
    } catch (e) {
      toast.error(t('workspace.delete.error'));
      console.error('Failed to delete workspace', e);
    }
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      submitButton={
        <Button color="danger" onClick={handleSubmit} disabled={!checked}>
          {t('common.action.delete')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose}>
          {t('common.action.close')}
        </Button>
      }
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      title={t('workspace.delete.title')}
    >
      <div className="p-4" style={{ width: '600px' }}>
        <p className={'pb-4'}>
          {t('workspace.delete.confirmLine1')}
          <br />
          {t('workspace.delete.confirmLine2')}
        </p>
        <label className="block">
          <input
            type="checkbox"
            className="form-checkbox mr-1 size-6 border-brand"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          {t('workspace.delete.checkLabel')}
        </label>
      </div>
    </GeneralModal>
  );
};
